import React, { useState } from 'react';
import { X, FileSpreadsheet, FileText, Download, Loader2, Check } from 'lucide-react';
import { reportsAPI } from '../../api/reports';
import { formatPlatformName } from '../../utils/platformHelpers';

const ExportModal = ({ campaignId, campaignName, dateRange, onClose }) => {
  const [exportType, setExportType] = useState('excel');
  const [includeOptions, setIncludeOptions] = useState({
    summary: true,
    creators: true,
    content: true,
    timeline: true,
    platforms: true
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Fetch export data from API
      const response = await reportsAPI.exportCampaignData(campaignId, {
        startDate: dateRange?.startDate?.toISOString(),
        endDate: dateRange?.endDate?.toISOString()
      });
      
      const data = response.data;
      
      if (exportType === 'excel') {
        await generateExcel(data);
      } else {
        await generatePDF(data);
      }
      
      setExportComplete(true);
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const generateExcel = async (data) => {
    // Using SheetJS (xlsx library)
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();

    // Summary sheet
    if (includeOptions.summary && data.summary) {
      const summaryData = [
        ['Campaign Report', data.campaign?.name || 'Campaign'],
        ['Generated', new Date().toLocaleDateString()],
        [''],
        ['Key Metrics', ''],
        ['Total Reach', data.summary.reach],
        ['Total Views', data.summary.views],
        ['VOD Views', data.summary.vodViews],
        ['Total Likes', data.summary.likes],
        ['Total Comments', data.summary.comments],
        ['Total Shares', data.summary.shares],
        ['Engagement Rate', `${data.summary.engagementRate?.toFixed(2)}%`],
        ['Average CCV', data.summary.avgCcv],
        ['Peak CCV', data.summary.peakCcv],
        ['Watch Hours', data.summary.watchHours],
        ['Content Pieces', data.summary.contentCount],
        ['Creators', data.summary.creatorCount],
        [''],
        ['ROI Metrics', ''],
        ['Total Spend', `£${data.roi?.totalSpend || 0}`],
        ['Cost Per View', `£${data.roi?.costPerView || 0}`],
        ['CPM', `£${data.roi?.costPer1000Views || 0}`],
        ['Cost Per Engagement', `£${data.roi?.costPerEngagement || 0}`],
        [''],
        ['YouTube data provided by the YouTube API Services. Data is valid for 30 days from the fetch date per YouTube API Terms of Service.']
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    }

    // Creators sheet
    if (includeOptions.creators && data.creators) {
      const creatorsData = [
        ['Creator', 'Handle', 'Platform', 'Followers/Subscribers', 'Tier', 'Views', 'VOD Views', 'Likes', 'Comments', 'Shares', 'Engagement Rate', 'Avg CCV', 'Peak CCV', 'Watch Hours', 'Spend', 'Cost Per View', 'Status']
      ];
      
      data.creators.forEach(c => {
        creatorsData.push([
          c.creator.name,
          c.creator.handle,
          formatPlatformName(c.creator.platform),
          c.creator.followerCount || 0,
          c.creator.tier || '',
          c.metrics.views,
          c.metrics.vodViews || 0,
          c.metrics.likes,
          c.metrics.comments,
          c.metrics.shares,
          `${c.metrics.engagementRate?.toFixed(2) || 0}%`,
          c.metrics.avgCcv || 0,
          c.metrics.peakCcv || 0,
          c.metrics.watchHours || 0,
          c.campaign?.agreedRate || 0,
          c.roi?.costPerView || 0,
          c.campaign?.status || ''
        ]);
      });
      
      const creatorsSheet = XLSX.utils.aoa_to_sheet(creatorsData);
      XLSX.utils.book_append_sheet(workbook, creatorsSheet, 'Creators');
    }

    // Content sheet
    if (includeOptions.content && data.content) {
      const contentData = [
        ['Title', 'Creator', 'Platform', 'Type', 'Post Date', 'Views', 'VOD Views', 'Likes', 'Comments', 'Shares', 'Engagement Rate', 'Avg CCV', 'Peak CCV', 'URL', 'Data Fetched On']
      ];

      data.content.forEach(c => {
        contentData.push([
          c.title || 'Untitled',
          c.creator?.name || '',
          formatPlatformName(c.platform),
          c.contentType,
          c.postDate ? new Date(c.postDate).toLocaleDateString() : '',
          c.metrics.views,
          c.metrics.vodViews || 0,
          c.metrics.likes,
          c.metrics.comments,
          c.metrics.shares,
          `${c.metrics.engagementRate?.toFixed(2) || 0}%`,
          c.metrics.avgCcv || 0,
          c.metrics.peakCcv || 0,
          c.contentUrl,
          c.lastScraped ? new Date(c.lastScraped).toLocaleDateString() : ''
        ]);
      });
      
      const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
      XLSX.utils.book_append_sheet(workbook, contentSheet, 'Content');
    }

    // Timeline sheet
    if (includeOptions.timeline && data.timeline) {
      const timelineData = [
        ['Period', 'Views', 'Likes', 'Comments', 'Shares', 'Engagement Rate', 'Avg CCV', 'Peak CCV', 'Watch Hours', 'Content Count']
      ];
      
      data.timeline.forEach(t => {
        timelineData.push([
          t.period,
          t.views,
          t.likes,
          t.comments,
          t.shares,
          `${t.engagementRate?.toFixed(2) || 0}%`,
          t.avgCcv || 0,
          t.peakCcv || 0,
          t.watchHours || 0,
          t.contentCount
        ]);
      });
      
      const timelineSheet = XLSX.utils.aoa_to_sheet(timelineData);
      XLSX.utils.book_append_sheet(workbook, timelineSheet, 'Timeline');
    }

    // Platforms sheet
    if (includeOptions.platforms && data.platforms) {
      const platformsData = [
        ['Platform', 'Creators', 'Content', 'Views', '% of Views', 'Likes', 'Comments', 'Shares', 'Engagement Rate', 'Avg CCV', 'Peak CCV', 'Watch Hours', 'Spend', 'Cost Per View']
      ];
      
      data.platforms.forEach(p => {
        platformsData.push([
          formatPlatformName(p.platform),
          p.creatorCount,
          p.contentCount,
          p.views,
          `${p.viewsPercentage}%`,
          p.likes,
          p.comments,
          p.shares,
          `${p.engagementRate?.toFixed(2) || 0}%`,
          p.avgCcv || 0,
          p.peakCcv || 0,
          p.watchHours || 0,
          p.spend,
          p.costPerView || 0
        ]);
      });
      
      const platformsSheet = XLSX.utils.aoa_to_sheet(platformsData);
      XLSX.utils.book_append_sheet(workbook, platformsSheet, 'Platforms');
    }

    // Download
    const fileName = `${campaignName?.replace(/[^a-z0-9]/gi, '_') || 'campaign'}_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const generatePDF = async (data) => {
    // Using jsPDF
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    
    const addPageIfNeeded = (requiredSpace = 30) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = 20;
      }
    };

    // Title
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(data.campaign?.name || 'Campaign Report', margin, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 20;

    // Summary section
    if (includeOptions.summary && data.summary) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text('Key Metrics', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const metrics = [
        ['Total Reach', formatNum(data.summary.reach)],
        ['Total Views', formatNum(data.summary.views)],
        ['Total Engagement', formatNum(data.summary.likes + data.summary.comments + data.summary.shares)],
        ['Engagement Rate', `${data.summary.engagementRate?.toFixed(2)}%`],
        ['Average CCV', formatNum(data.summary.avgCcv)],
        ['Peak CCV', formatNum(data.summary.peakCcv)],
        ['Watch Hours', formatNum(data.summary.watchHours)],
        ['Content Pieces', data.summary.contentCount],
        ['Creators', data.summary.creatorCount]
      ];
      
      metrics.forEach(([label, value]) => {
        doc.text(`${label}: ${value}`, margin, yPos);
        yPos += 6;
      });
      
      yPos += 10;
    }

    // ROI section
    if (includeOptions.summary && data.roi) {
      addPageIfNeeded(50);
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('ROI Analysis', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const roiMetrics = [
        ['Total Spend', `£${formatNum(data.roi.totalSpend)}`],
        ['Cost Per View', `£${data.roi.costPerView}`],
        ['CPM', `£${data.roi.costPer1000Views}`],
        ['Cost Per Engagement', `£${data.roi.costPerEngagement}`]
      ];
      
      roiMetrics.forEach(([label, value]) => {
        doc.text(`${label}: ${value}`, margin, yPos);
        yPos += 6;
      });
      
      yPos += 15;
    }

    // Top Creators
    if (includeOptions.creators && data.creators?.length > 0) {
      addPageIfNeeded(80);
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Top Creators', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      // Table header
      doc.setFont('helvetica', 'bold');
      doc.text('Creator', margin, yPos);
      doc.text('Platform', margin + 50, yPos);
      doc.text('Views', margin + 80, yPos);
      doc.text('Engagement', margin + 110, yPos);
      doc.text('Rate', margin + 145, yPos);
      yPos += 6;
      
      doc.setFont('helvetica', 'normal');
      
      // Top 10 creators
      data.creators.slice(0, 10).forEach(c => {
        addPageIfNeeded(8);
        doc.text(c.creator.name.substring(0, 20), margin, yPos);
        doc.text(formatPlatformName(c.creator.platform) || '', margin + 50, yPos);
        doc.text(formatNum(c.metrics.views), margin + 80, yPos);
        doc.text(formatNum(c.metrics.totalEngagement), margin + 110, yPos);
        doc.text(`${c.metrics.engagementRate?.toFixed(1) || 0}%`, margin + 145, yPos);
        yPos += 6;
      });
      
      yPos += 10;
    }

    // Platform breakdown
    if (includeOptions.platforms && data.platforms?.length > 0) {
      addPageIfNeeded(60);
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Platform Breakdown', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(9);
      
      // Table header
      doc.setFont('helvetica', 'bold');
      doc.text('Platform', margin, yPos);
      doc.text('Views', margin + 40, yPos);
      doc.text('% Share', margin + 70, yPos);
      doc.text('Engagement', margin + 100, yPos);
      doc.text('Spend', margin + 140, yPos);
      yPos += 6;
      
      doc.setFont('helvetica', 'normal');
      
      data.platforms.forEach(p => {
        addPageIfNeeded(8);
        doc.text(formatPlatformName(p.platform) || '', margin, yPos);
        doc.text(formatNum(p.views), margin + 40, yPos);
        doc.text(`${p.viewsPercentage}%`, margin + 70, yPos);
        doc.text(formatNum(p.likes + p.comments + p.shares), margin + 100, yPos);
        doc.text(`£${formatNum(p.spend)}`, margin + 140, yPos);
        yPos += 6;
      });
    }

    // YouTube API ToS disclaimer
    addPageIfNeeded(20);
    yPos += 10;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(128);
    doc.text('YouTube data provided by the YouTube API Services. Data is valid for 30 days from the fetch date per YouTube API Terms of Service.', margin, yPos);
    doc.setTextColor(0);

    // Download
    const fileName = `${campaignName?.replace(/[^a-z0-9]/gi, '_') || 'campaign'}_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const formatNum = (num) => {
    if (!num) return '0';
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const toggleOption = (key) => {
    setIncludeOptions({ ...includeOptions, [key]: !includeOptions[key] });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Export Campaign Report</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Export type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Export Format
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setExportType('excel')}
                className={`flex items-center justify-center px-4 py-3 rounded-xl border-2 transition-colors ${
                  exportType === 'excel'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <FileSpreadsheet className="w-5 h-5 mr-2" />
                Excel (.xlsx)
              </button>
              <button
                onClick={() => setExportType('pdf')}
                className={`flex items-center justify-center px-4 py-3 rounded-xl border-2 transition-colors ${
                  exportType === 'pdf'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <FileText className="w-5 h-5 mr-2" />
                PDF (.pdf)
              </button>
            </div>
          </div>

          {/* Include options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Include Sections
            </label>
            <div className="space-y-2">
              {[
                { key: 'summary', label: 'Summary & ROI' },
                { key: 'creators', label: 'Creator Breakdown' },
                { key: 'content', label: 'Content List' },
                { key: 'timeline', label: 'Timeline Data' },
                { key: 'platforms', label: 'Platform Breakdown' }
              ].map(option => (
                <label
                  key={option.key}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm text-gray-700">{option.label}</span>
                  <input
                    type="checkbox"
                    checked={includeOptions[option.key]}
                    onChange={() => toggleOption(option.key)}
                    className="w-4 h-4 text-sc-pink rounded focus:ring-sc-pink"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* YouTube API attribution */}
          <div className="mt-4 flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
            <svg viewBox="0 3.5 24 17" fill="#FF0000" className="h-[21px] w-auto flex-shrink-0 mt-0.5" role="img" aria-label="YouTube">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <p className="text-xs text-gray-500">
              YouTube data provided by the YouTube API Services. Data is valid for 30 days from the fetch date per YouTube API Terms of Service.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || !Object.values(includeOptions).some(v => v)}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              isExporting || exportComplete
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-sc-pink text-white hover:bg-sc-pink/90'
            }`}
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : exportComplete ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Downloaded!
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
